/**
 * Upcoming events, for the community page's right rail.
 *
 * Reads the same public catalog the calendar reads rather than carrying its own
 * list, so an event created in the CRM appears here without anyone remembering
 * this file exists.
 *
 * Each row carries a round speaker portrait, the same treatment the events page
 * uses in its speaker filter: the rail is a list of people to hear, so a face
 * belongs there rather than a crop of event artwork. Where no portrait resolves,
 * the circle falls back to the speaker's initials tinted with the event's own
 * colour, so a row is never an empty hole.
 */
import { useMemo } from "react";
import { Link } from "wouter";
import { ArrowRight, CalendarDays, MapPin, Video } from "lucide-react";
import { trpc } from "@/lib/trpc";
import type { CommunityPageContent } from "@shared/communityPage";
import { resolveSpeakerPortrait } from "@/lib/speakerPortraits";
import SpeakerAvatar from "@/components/SpeakerAvatar";
import type { CHIEvent } from "@/lib/eventsData";

/** How many events the rail shows before deferring to the full calendar. */
const RAIL_LIMIT = 4;

/** Today in the same YYYY-MM-DD shape the catalog stores, compared as text. */
function todayIso() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

/** "Aug 12, 2026" split into the stacked month/day chip. */
function dateChip(event: { date: string; displayDate: string }) {
  const parsed = event.date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!parsed) return { month: "", day: event.displayDate };
  const date = new Date(Number(parsed[1]), Number(parsed[2]) - 1, Number(parsed[3]));
  return {
    month: date.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
    day: String(date.getDate()),
  };
}

export default function CommunityEventsSidebar({ content }: { content?: CommunityPageContent["sidebar"] } = {}) {
  const catalog = trpc.events.publicCatalog.useQuery(undefined, { staleTime: 60_000 });
  // Portraits are a nicety here, so a failure must not take the rail down with it.
  const speakersQuery = trpc.speakers.publicList.useQuery(undefined, { retry: false });
  const publishedSpeakers = speakersQuery.data ?? [];

  const upcoming = useMemo(() => {
    const cutoff = todayIso();
    return ((catalog.data ?? []) as CHIEvent[])
      .filter(event => typeof event.date === "string" && event.date >= cutoff)
      .sort((a, b) => a.date.localeCompare(b.date) || String(a.time ?? "").localeCompare(String(b.time ?? "")))
      .slice(0, RAIL_LIMIT);
  }, [catalog.data]);

  return (
    <section className="community-rail" aria-labelledby="community-rail-events">
      <header className="community-rail__head">
        <h2 id="community-rail-events">{content?.eventsTitle ?? "Upcoming events"}</h2>
        <Link href="/calendar-of-events">{content?.eventsViewAllLabel ?? "View all"}</Link>
      </header>

      {catalog.isLoading && <p className="community-rail__empty">Loading events&hellip;</p>}

      {catalog.error && (
        <p className="community-rail__empty" role="alert">Upcoming events could not be loaded.</p>
      )}

      {!catalog.isLoading && !catalog.error && upcoming.length === 0 && (
        <p className="community-rail__empty">{content?.eventsEmptyLabel ?? "No upcoming events are scheduled yet."}</p>
      )}

      <ul className="community-rail__list">
        {upcoming.map(event => {
          const chip = dateChip(event);
          /*
           * Portrait first here, unlike the event cards: this circle is a person,
           * so a published portrait outranks the event's hero art, and the hero is
           * only offered as the resolver's last resort.
           */
          const portrait = resolveSpeakerPortrait({
            eventSlug: event.slug,
            speakerName: event.speaker ?? "",
            heroImage: event.heroImage,
            publishedSpeakers,
          });

          return (
            <li key={event.slug}>
              <Link className="community-rail__item" href={`/events/${event.slug}`}>
                <span className="community-rail__chip" aria-hidden="true">
                  <em>{chip.month}</em>
                  <strong>{chip.day}</strong>
                </span>

                <span className="community-rail__copy">
                  <span className="community-rail__title">{event.title}</span>
                  <span className="community-rail__meta">
                    <CalendarDays size={11} aria-hidden="true" />
                    {event.displayDate}
                  </span>
                  <span className="community-rail__meta">
                    {event.type === "virtual"
                      ? <><Video size={11} aria-hidden="true" />Online</>
                      : <><MapPin size={11} aria-hidden="true" />{event.location || "In person"}</>}
                  </span>
                </span>

                <SpeakerAvatar
                  name={event.speaker ?? ""}
                  portraitUrl={portrait}
                  className="community-rail__avatar"
                  fallbackClassName="community-rail__avatar--fallback"
                  fallbackStyle={{ background: `${event.color}2b`, borderColor: `${event.color}80` }}
                />
              </Link>
            </li>
          );
        })}
      </ul>

      <Link className="community-rail__cta" href="/calendar-of-events">
        {content?.eventsCalendarLabel ?? "See the full calendar"} <ArrowRight size={14} aria-hidden="true" />
      </Link>

      <style>{`
        .community-rail{position:sticky;top:92px;display:grid;gap:.85rem;padding:1.1rem 1.15rem;border:1px solid rgba(255,255,255,.09);border-radius:1rem;background:rgba(10,16,30,.84);box-shadow:inset 0 1px 0 rgba(255,255,255,.04)}
        .community-rail__head{display:flex;align-items:center;justify-content:space-between;gap:1rem}
        .community-rail__head h2{margin:0;color:#fff;font-family:'Space Grotesk',sans-serif;font-size:.8rem;font-weight:800;letter-spacing:.05em;text-transform:uppercase}
        .community-rail__head a{color:#F9A8D4;font-family:'Inter',sans-serif;font-size:.75rem;font-weight:700;text-decoration:none}
        .community-rail__head a:hover,.community-rail__head a:focus-visible{color:#fff;text-decoration:underline}
        .community-rail__empty{margin:0;color:#94A3B8;font-family:'Inter',sans-serif;font-size:.8rem;line-height:1.55}
        .community-rail__list{display:grid;gap:.3rem;margin:0;padding:0;list-style:none}
        .community-rail__item{display:grid;grid-template-columns:2.6rem minmax(0,1fr) 2.75rem;align-items:center;gap:.7rem;padding:.6rem .35rem;border-radius:.6rem;text-decoration:none;transition:background .16s}
        .community-rail__item:hover,.community-rail__item:focus-visible{outline:0;background:rgba(255,255,255,.05)}
        .community-rail__chip{display:grid;justify-items:center;gap:.05rem;padding:.3rem .1rem;border:1px solid rgba(233,30,140,.28);border-radius:.5rem;background:rgba(233,30,140,.1)}
        .community-rail__chip em{color:#F9A8D4;font-family:'Space Grotesk',sans-serif;font-size:.55rem;font-style:normal;font-weight:850;letter-spacing:.07em}
        .community-rail__chip strong{color:#fff;font-family:'Space Grotesk',sans-serif;font-size:1.02rem;font-weight:750;line-height:1}
        .community-rail__copy{display:grid;gap:.18rem;min-width:0}
        .community-rail__title{overflow:hidden;color:#fff;font-family:'Space Grotesk',sans-serif;font-size:.83rem;font-weight:700;letter-spacing:-.01em;line-height:1.3;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:2}
        .community-rail__meta{display:flex;align-items:center;gap:.3rem;overflow:hidden;color:#94A3B8;font-family:'Inter',sans-serif;font-size:.71rem;white-space:nowrap;text-overflow:ellipsis}
        .community-rail__meta svg{flex:0 0 auto;color:#64748B}
        /* Same circle the events page speaker filter uses, at rail scale. */
        .community-rail__avatar{width:2.75rem;height:2.75rem;flex:0 0 2.75rem;border:1px solid rgba(255,255,255,.22);border-radius:999px;object-fit:cover;object-position:center top;background:#0a1020}
        .community-rail__avatar--fallback{display:inline-flex;align-items:center;justify-content:center;color:#F8FAFC;font-family:'Space Grotesk',sans-serif;font-size:.78rem;font-weight:800;letter-spacing:.02em}
        .community-rail__cta{display:inline-flex;align-items:center;justify-content:center;gap:.4rem;min-height:2.3rem;margin-top:.15rem;border:1px solid rgba(255,255,255,.12);border-radius:.6rem;color:#E2E8F0;background:rgba(255,255,255,.03);font-family:'Inter',sans-serif;font-size:.77rem;font-weight:700;text-decoration:none;transition:background .16s,border-color .16s}
        .community-rail__cta:hover,.community-rail__cta:focus-visible{outline:0;border-color:rgba(244,114,182,.5);background:rgba(233,30,140,.1);color:#fff}
        @media(max-width:1100px){.community-rail{position:static;top:auto}}
      `}</style>
    </section>
  );
}
